import orderBy from 'lodash/orderBy';
import Vec2 from 'vec2';

import { DEFAULT_BUMPOUT_DEPTH, DEFAULT_DEPTH } from '../constants';
import { isBumpOut, isRadiusOut } from '../enums';
import { roundToArrowLengthGranularity } from '../helpers';
import { getMidpoint } from '../helpers/MathFunctions.utils';
import { ArrowInfo, DrawingEdge } from '../models';
import { getArrowShapeFromSegment } from './getArrowShapeFromSegment';
import { hasRequiredProps } from './hasRequiredProps';

import { isSomething } from '~/core/utils';

const calcBumpOutArrowStart = (
  edge: Omit<DrawingEdge, 'bumpOut'> & Pick<Required<DrawingEdge>, 'bumpOut'>
): Vec2 =>
  edge.startDisplayVec
    .add(
      edge.dirVec
        .normalize(true)
        .multiply(edge.bumpOut.clockwiseRemainderLength, true),
      true
    )
    .add(
      edge.dirVec.normalize(true).multiply(edge.bumpOut.length / 2, true),
      true
    );

const calculateDistance = (startPt: Vec2, endPt: Vec2): number => {
  const lengthX = startPt.x - endPt.x;
  const lengthY = startPt.y - endPt.y;

  return Math.sqrt(lengthX * lengthX + lengthY * lengthY);
};

const getSmallestAdjacentEdge = (
  firstEdgeDimension: number,
  secondEdgeDimension: number
): number => {
  const minEdge = Math.min(firstEdgeDimension, secondEdgeDimension);

  return Math.sqrt(2) * (minEdge - 1);
};

const calculateDiagonalMaxLength = (
  newPoints: Vec2[],
  i: number
): number | undefined => {
  let prevEdgeStart = newPoints[i - 2];
  let prevEdgeEnd = newPoints[i - 1];

  if (i === 1) {
    //  when i === 1, one of the adjacent edges with the diagonal corner is the last edge
    prevEdgeStart = newPoints[newPoints.length - 2];
    prevEdgeEnd = newPoints[newPoints.length - 1];
  }

  const prevStart = new Vec2(prevEdgeStart.x, prevEdgeStart.y);
  const prevEnd = new Vec2(prevEdgeEnd.x, prevEdgeEnd.y);
  const nextStart = new Vec2(newPoints[i].x, newPoints[i].y);
  const nextEnd = new Vec2(newPoints[i + 1].x, newPoints[i + 1].y);

  const prevLength = calculateDistance(prevStart, prevEnd);
  const nextLength = calculateDistance(nextStart, nextEnd);
  const currentLength = calculateDistance(newPoints[i - 1], newPoints[i]);

  const diagonalMaximumLength = getSmallestAdjacentEdge(prevLength, nextLength);

  return (
    roundToArrowLengthGranularity(diagonalMaximumLength) +
    roundToArrowLengthGranularity(currentLength)
  );
};

const adjustPointsForEdgeFeatures = (
  startPt: Vec2,
  endPt: Vec2,
  edge: DrawingEdge | undefined
): void => {
  if (!edge) {
    return;
  }

  const radiusDepth = edge.radiusSagitta;
  const bumpDepth = edge.bumpOut?.depth;
  const hasRadius = isRadiusOut(edge) && radiusDepth;
  const hasBumpOut = isBumpOut(edge) && bumpDepth;

  [startPt, endPt].forEach((pt) => {
    if (hasRadius) {
      pt.add(edge.normalVec.multiply(radiusDepth, true), false);
    } else if (hasBumpOut) {
      const depthTimes4 = DEFAULT_BUMPOUT_DEPTH * 4;
      const vec = edge.normalVec.multiply(depthTimes4, true);

      pt.add(edge.normalVec.multiply(bumpDepth, true).add(vec, false));
    }
  });
};

const createExteriorArrow = (
  startPt: Vec2,
  endPt: Vec2,
  diagonalCorners: { diagonals: boolean[]; cornerIds: (string | undefined)[] },
  matchingEdges: (DrawingEdge | undefined)[],
  i: number,
  newPoints: Vec2[]
): ArrowInfo => {
  const j = i === newPoints.length - 1 ? 0 : i;
  const arrowShapePoints = getArrowShapeFromSegment(startPt, endPt);
  const length = calculateDistance(startPt, endPt);

  const diagonalMaximumLength = diagonalCorners.diagonals[j]
    ? calculateDiagonalMaxLength(newPoints, i)
    : undefined;

  return {
    path: arrowShapePoints,
    isInterior: false,
    size: roundToArrowLengthGranularity(length),
    isDiagonalCorner: diagonalCorners.diagonals[j],
    drawingEdgeId:
      matchingEdges[j] !== undefined && !diagonalCorners.diagonals[i]
        ? matchingEdges[j]?.id
        : undefined,
    isVisibleInCanvas: true,
    isActiveForEdgeCalc: true,
    cornerId: diagonalCorners.diagonals[j]
      ? diagonalCorners.cornerIds[j]
      : undefined,
    maximumLength: diagonalMaximumLength,
    label: '',
  };
};

const createInteriorArrowsForFeatures = (
  matchingEdges: (DrawingEdge | undefined)[]
): ArrowInfo[] => {
  const arrows: ArrowInfo[] = [];

  matchingEdges
    .filter(isSomething)
    .filter(
      (edge) =>
        (isRadiusOut(edge) && hasRequiredProps(edge, ['radiusSagitta'])) ||
        (isBumpOut(edge) && hasRequiredProps(edge, ['bumpOut']))
    )
    .forEach((edge) => {
      const [intArrowLen, arrowStartPt]: [number | undefined, Vec2] =
        isBumpOut(edge) && hasRequiredProps(edge, ['bumpOut'])
          ? [edge.bumpOut.depth, calcBumpOutArrowStart(edge)]
          : [
              edge.radiusSagitta,
              getMidpoint(edge.startDisplayVec, edge.endDisplayVec),
            ];

      if (!intArrowLen) {
        return;
      }

      const arrowEndPt = arrowStartPt.add(
        edge.normalVec.multiply(intArrowLen, true),
        true
      );
      const arrowShapePoints = getArrowShapeFromSegment(
        arrowStartPt,
        arrowEndPt,
        true
      );

      arrows.push({
        path: arrowShapePoints,
        isInterior: true,
        size: roundToArrowLengthGranularity(intArrowLen),
        isVisibleInCanvas: true,
        isActiveForEdgeCalc: true,
        drawingEdgeId: edge.id,
        isRadiusOut: isRadiusOut(edge),
        isBumpOut: isBumpOut(edge),
        label: '',
      });
    });

  return arrows;
};

const buildInteriorArrows = (points: Vec2[]): ArrowInfo[] => {
  const arrows: ArrowInfo[] = [];
  const length = points.length;

  if (length <= 6) {
    return [];
  }

  for (let i = 1; i < length / 2 - 2; i++) {
    let depth = DEFAULT_DEPTH;
    const firstEdgeX = points[i + 1].x - points[i].x;
    const firstEdgeY = points[i + 1].y - points[i].y;

    const firstEdgeLength = Math.sqrt(
      firstEdgeX * firstEdgeX + firstEdgeY * firstEdgeY
    );
    const normalizedFirstEdgeX =
      firstEdgeLength > 0 ? firstEdgeX / firstEdgeLength : 0;
    const normalizedFirstEdgeY =
      firstEdgeLength > 0 ? firstEdgeY / firstEdgeLength : 0;

    const normalVec = new Vec2(-normalizedFirstEdgeY, normalizedFirstEdgeX);

    const diaVecX = points[i + 1].x - points[points.length - i - 2].x;
    const diaVecY = points[i + 1].y - points[points.length - i - 2].y;
    const diaVec = new Vec2(diaVecX, diaVecY);

    depth = Math.abs(normalVec.x * diaVec.x + normalVec.y * diaVec.y);

    const midPt = points[i + 1].add(points[i], true).multiply(0.5);
    const secondPt = midPt.subtract(normalVec.multiply(depth, true), true);
    const interiorSegment = [midPt, secondPt];

    const arrowShapePoints = getArrowShapeFromSegment(
      interiorSegment[0],
      interiorSegment[1],
      true
    );
    const segmentLength = calculateDistance(
      interiorSegment[0],
      interiorSegment[1]
    );

    arrows.push({
      path: arrowShapePoints,
      isInterior: true,
      size: roundToArrowLengthGranularity(segmentLength),
      isVisibleInCanvas: true,
      isActiveForEdgeCalc: true,
      interiorSkIndex: i,
      label: '',
    });
  }

  return arrows;
};

/**
 * Generates measurement arrows for a countertop shape, including exterior dimensions,
 * interior measurements, and special features (radius/bump outs).
 *
 * @param pointsForExteriorArrows - Vertices defining the outer shape perimeter
 * @param pointsForInteriorArrows - Vertices used for interior measurement calculations
 * @param diagonalCorners - Specifies which corners are diagonal and their IDs
 * @param matchingEdges - Edge definitions containing special features (radius/bump outs)
 * @returns Array of ArrowInfo objects representing all measurement arrows
 */
export const getArrowsFromOutlines = (
  pointsForExteriorArrows: Vec2[],
  pointsForInteriorArrows: Vec2[],
  diagonalCorners: { diagonals: boolean[]; cornerIds: (string | undefined)[] },
  matchingEdges: (DrawingEdge | undefined)[]
): ArrowInfo[] => {
  let arrows: ArrowInfo[] = [];

  const newPoints = [
    ...pointsForExteriorArrows.slice(0),
    pointsForExteriorArrows[0],
  ];

  for (let i = 1; i < newPoints.length; i++) {
    const startPt = new Vec2(newPoints[i - 1].x, newPoints[i - 1].y);
    const endPt = new Vec2(newPoints[i].x, newPoints[i].y);

    if (matchingEdges[i - 1] !== undefined && !diagonalCorners.diagonals[i]) {
      adjustPointsForEdgeFeatures(startPt, endPt, matchingEdges[i - 1]);
    }

    const arrow = createExteriorArrow(
      startPt,
      endPt,
      diagonalCorners,
      matchingEdges,
      i,
      newPoints
    );

    arrows.push(arrow);
  }

  arrows = arrows.concat(buildInteriorArrows(pointsForInteriorArrows));
  arrows = arrows.concat(createInteriorArrowsForFeatures(matchingEdges));
  arrows = orderBy(arrows, ['isDiagonalCorner'], ['asc']);

  return arrows;
};
